import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Paper,
  Chip,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Container,
} from '@mui/material';
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format, isSameDay } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { CreateBookingSlot } from '../../types/bookingSlot';
import { BarberServices } from '../../types/barber';

export interface BookingFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

interface PublicBookingCalendarProps {
  title: string;
  subtitle: string;
  loading: boolean;
  error: string | null;
  slots: CreateBookingSlot[];
  showBarberName?: boolean;
  getBarberName: (barberId: bigint) => string;
  getService: (serviceId: bigint) => BarberServices | undefined;
  onBook: (slot: CreateBookingSlot, formData: BookingFormData) => Promise<void>;
}

const PublicBookingCalendar: React.FC<PublicBookingCalendarProps> = ({
  title,
  subtitle,
  loading,
  error,
  slots,
  showBarberName = false,
  getBarberName,
  getService,
  onBook,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<CreateBookingSlot | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [formData, setFormData] = useState<BookingFormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
  });

  const getSlotsForDate = (date: Date) =>
    slots.filter((slot) => isSameDay(slot.slot_date, date));

  const hasAvailableSlots = (date: Date) => getSlotsForDate(date).length > 0;

  const handleDateSelect = (value: Date | null) => {
    if (!value) return;
    setSelectedDate(value);
  };

  const handleSlotClick = (slot: CreateBookingSlot) => {
    setSelectedSlot(slot);
    setIsDialogOpen(true);
    setFormData({ customerName: '', customerEmail: '', customerPhone: '' });
    setSuccessMessage('');
    setBookingError(null);
  };

  const handleCreateReservation = async () => {
    if (
      !selectedSlot ||
      !formData.customerName ||
      !formData.customerEmail ||
      !formData.customerPhone
    ) {
      return;
    }

    try {
      setSubmitting(true);
      setBookingError(null);
      await onBook(selectedSlot, formData);
      setSuccessMessage(
        'Reservation created successfully! Confirmation emails sent.'
      );

      setTimeout(() => {
        setIsDialogOpen(false);
        setFormData({ customerName: '', customerEmail: '', customerPhone: '' });
        setSelectedSlot(null);
        setSuccessMessage('');
      }, 2000);
    } catch (err) {
      console.error('Failed to create reservation:', err);
      setBookingError('Failed to create reservation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const DayWithSlots = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, onDaySelect } = props;
    const hasSlots = !outsideCurrentMonth && hasAvailableSlots(day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isPastDate = day < today;

    return (
      <PickersDay
        isFirstVisibleCell={true}
        isLastVisibleCell={true}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disabled={isPastDate}
        sx={{
          ...(hasSlots && {
            backgroundColor: 'rgba(76, 175, 80, 0.3)',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.5)' },
          }),
        }}
        onDaySelect={onDaySelect}
      />
    );
  };

  const selectedDateSlots = selectedDate ? getSlotsForDate(selectedDate) : [];

  return (
    <Container
      sx={{
        marginTop: '75px !important',
        width: '100%',
        padding: '0px 15px !important',
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            mb: 3,
            p: 3,
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            borderRadius: 2,
            borderLeft: '5px solid #dd8709',
            color: 'white',
          }}
        >
          <Typography variant="h4" sx={{ fontFamily: 'GFS Didot' }}>
            {title}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateCalendar
                  value={selectedDate}
                  onChange={handleDateSelect}
                  slots={{ day: DayWithSlots }}
                  disabled={loading}
                />
              </LocalizationProvider>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            {selectedDate && (
              <Paper sx={{ p: 2, maxHeight: 400, overflowY: 'auto' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Available Slots - {format(selectedDate, 'MMMM d, yyyy')}
                </Typography>

                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedDateSlots.length === 0 ? (
                  <Typography color="text.secondary">
                    No available slots for this date
                  </Typography>
                ) : (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {selectedDateSlots.map((slot) => {
                      const service = getService(slot.service_id);
                      return (
                        <Card
                          key={slot.id}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 },
                            transition: 'box-shadow 0.3s',
                          }}
                          onClick={() => handleSlotClick(slot)}
                        >
                          <CardContent>
                            {showBarberName && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  mb: 1,
                                }}
                              >
                                <PersonIcon fontSize="small" />
                                <Typography variant="body2">
                                  {getBarberName(slot.barber_id)}
                                </Typography>
                              </Box>
                            )}
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              {service?.name || 'Service'}
                            </Typography>
                            <Box
                              sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}
                            >
                              <Chip
                                icon={<AttachMoneyIcon />}
                                label={`£${service?.price || '0'}`}
                                size="small"
                              />
                              <Chip
                                icon={<AccessTimeIcon />}
                                label={`${service?.duration || 0} min`}
                                size="small"
                              />
                              <Chip
                                label={format(slot.start_time, 'HH:mm')}
                                size="small"
                                color="primary"
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>

        <Dialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Reservation</DialogTitle>
          <DialogContent>
            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}
            {bookingError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {bookingError}
              </Alert>
            )}

            {selectedSlot && (
              <>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Selected Slot
                  </Typography>
                  <Typography variant="body1">
                    {getService(selectedSlot.service_id)?.name} -{' '}
                    {format(selectedSlot.start_time, 'HH:mm')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {format(selectedSlot.slot_date, 'MMMM d, yyyy')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Customer Name"
                    value={formData.customerName}
                    onChange={(e) =>
                      setFormData({ ...formData, customerName: e.target.value })
                    }
                    fullWidth
                    required
                    disabled={submitting || !!successMessage}
                  />
                  <TextField
                    label="Customer Email"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerEmail: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    disabled={submitting || !!successMessage}
                  />
                  <TextField
                    label="Customer Phone"
                    value={formData.customerPhone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerPhone: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    disabled={submitting || !!successMessage}
                    placeholder="07980123456"
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsDialogOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateReservation}
              variant="contained"
              disabled={
                submitting ||
                !formData.customerName ||
                !formData.customerEmail ||
                !formData.customerPhone ||
                !!successMessage
              }
            >
              {submitting ? 'Creating...' : 'Create Reservation'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default PublicBookingCalendar;
